// ✅ Updated NewContentForm.js
// frontend/src/features/content/NewContentForm.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useAddNewContentMutation } from './contentApiSlice';
import { useGetAllCategoriesAdminQuery, selectAllCategoriesForAdminByArgs } from '../categories/categoriesApiSlice';
import { useSelector } from 'react-redux';
import useTitle from '../../hooks/useTitle';

const NewContentForm = () => {
    useTitle('Nirakar Satguru: Create New Content');
    const navigate = useNavigate();

    const [addNewContent, { isLoading, isSuccess, isError, error }] = useAddNewContentMutation();

    const [title, setTitle] = useState('');
    
    const [textContent, setTextContent] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [contentType, setContentType] = useState('text');
    const [Clanguage, setLanguage] = useState('hi');

    const { data: categoriesResult, isLoading: isCategoriesLoading } = useGetAllCategoriesAdminQuery({ page: 1, search: '', isActive: 'true' });
    const categories = useSelector(state => selectAllCategoriesForAdminByArgs(state, { page: 1, search: '', isActive: 'true' }));

    useEffect(() => {
        if (imageFile) {
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(imageFile);
        } else {
            setImagePreview('');
        }
    }, [imageFile]);

    useEffect(() => {
        if (isSuccess) {
            setTitle('');
            
            setTextContent('');
            setSelectedCategory('');
            setImageFile(null);
            setImagePreview('');
            setIsActive(true);
            setContentType('text');
            setLanguage('hi');
            navigate('/dashboard/contents');
        }
    }, [isSuccess, navigate]);

    const canSave = [title,  textContent, selectedCategory, contentType, Clanguage].every(Boolean) && !isLoading;

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setImageFile(e.target.files[0]);
        } else {
            setImageFile(null);
            setImagePreview('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSave) return;

        const formData = new FormData();
        formData.append('title', title);
       
        formData.append('text_content', textContent);
        formData.append('category', selectedCategory);
        formData.append('isActive', isActive);
        formData.append('contentType', contentType);
        formData.append('Clanguage', Clanguage);
        if (imageFile) formData.append('file', imageFile);

        try {
            await addNewContent(formData).unwrap();
        } catch (err) {
            console.error('Failed to create new content:', err);
        }
    };

    if (isCategoriesLoading) {
        return <div className="text-center my-5"><Spinner animation="border" size="sm" /><p className="mt-2">Loading categories for form...</p></div>;
    }

    return (
        <div className="container mt-4">
            <h2>Add New Content</h2>
            {isError && <Alert variant="danger">{error?.data?.message || 'Failed to create new content.'}</Alert>}
            {isSuccess && <Alert variant="success">Content created successfully!</Alert>}

            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="title">
                    <Form.Label>Title:</Form.Label>
                    <Form.Control type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
                </Form.Group>

              

                <Form.Group className="mb-3" controlId="textContent">
                    <Form.Label>Main Text Content:</Form.Label>
                    <Form.Control as="textarea" rows={5} value={textContent} onChange={(e) => setTextContent(e.target.value)} required />
                </Form.Group>

                <Form.Group className="mb-3" controlId="category">
                    <Form.Label>Category:</Form.Label>
                    <Form.Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} required>
                        <option value="">Select a category</option>
                        {categories?.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" controlId="contentType">
                    <Form.Label>Content Type:</Form.Label>
                    <Form.Select value={contentType} onChange={(e) => setContentType(e.target.value)} required>
                        <option value="">Select content type</option>
                        <option value="text">Text</option>
                        <option value="audio">Audio</option>
                        <option value="video">Video</option>
                        <option value="pdf">PDF</option>
                        <option value="image">Image</option>
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" controlId="Clanguage">
                    <Form.Label>Language:</Form.Label>
                    <Form.Select value={Clanguage} onChange={(e) => setLanguage(e.target.value)} required>
                        <option value="hi">Hindi</option>
                        <option value="en">English</option>
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" controlId="imageFile">
                    <Form.Label>Content Image (Optional):</Form.Label>
                    <Form.Control type="file" onChange={handleFileChange} accept="image/*" />
                    {imagePreview && <img src={imagePreview} alt="Preview" style={{ maxWidth: '150px', height: 'auto' }} className="rounded mt-2" />}
                </Form.Group>

                <Form.Group className="mb-3" controlId="isActive">
                    <Form.Check type="checkbox" label="Is Active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                </Form.Group>

                <Button variant="primary" type="submit" disabled={!canSave}>
                    {isLoading ? <Spinner animation="border" size="sm" /> : 'Create Content'}
                </Button>
                <Button variant="secondary" onClick={() => navigate('/dashboard/contents')} className="ms-2">Cancel</Button>
            </Form>
        </div>
    );
};

export default NewContentForm;
